import React, { lazy, Suspense, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { INCIDENT } from 'components/IAP/ICSConstants';
import { useIncidentActions } from 'components/IAP/hooks/incidentActionHooks';
import type { ActionEntity } from 'components/IAP/hooks/useScopedActionForm';
import {
  actionStatusBadgeVariant,
  formatActionDate,
  isActionOverdue,
} from 'components/IAP/actionsTable/actionEditModel';
import { useActionTicketsFlag } from 'utils/featureFlags';

/**
 * Loaded on click: the drawer pulls in the map and checklist stacks, which the
 * roster modal has no reason to pay for just to list a seat's tasks.
 */
const ActionDrawer = lazy(() => import('components/IAP/ActionDrawer'));

type PositionTasksSectionProps = {
  positionId: string;
  positionName?: string;
};

/**
 * The tasks assigned to this seat, shown inside the roster position modal so
 * whoever is filling the seat can see the work that comes with it without
 * leaving for the tracker.
 *
 * Renders nothing when the `ACTION_TICKETS` flag is off or the seat has no
 * tasks, which keeps `EditPositionModal` to one unconditional line.
 */
const PositionTasksSection: React.FC<PositionTasksSectionProps> = ({
  positionId,
  positionName,
}) => {
  const tickets = useActionTicketsFlag();
  const { data: actions = [] } = useIncidentActions(INCIDENT);
  const [openAction, setOpenAction] = useState<ActionEntity | null>(null);

  const assigned = useMemo(
    () =>
      (actions as ActionEntity[]).filter(
        (action) =>
          !action.archived &&
          action.assignee_kind === 'position' &&
          action.assignee_id === positionId
      ),
    [actions, positionId]
  );

  if (!tickets || assigned.length === 0) return null;

  const openCount = assigned.filter(
    (action) => action.status !== 'Closed'
  ).length;

  return (
    <section
      className="tw:mb-3"
      aria-label={
        positionName ? `Tasks assigned to ${positionName}` : 'Assigned tasks'
      }
    >
      <div className="tw:mb-2 tw:flex tw:items-center tw:justify-between tw:gap-2">
        <span className="tw:font-semibold">Assigned Tasks</span>
        <span className="tw:text-sm tw:text-muted-foreground">
          {openCount} open of {assigned.length}
        </span>
      </div>
      <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-1 tw:p-0">
        {assigned.map((action) => {
          const overdue = isActionOverdue(action.status, action.target_date);
          return (
            <li key={action.id}>
              <button
                type="button"
                className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-border tw:bg-transparent tw:px-2 tw:py-1.5 tw:text-left tw:hover:bg-accent"
                onClick={() => setOpenAction(action)}
              >
                <span className="tw:w-24 tw:shrink-0 tw:truncate tw:font-mono tw:text-xs tw:text-muted-foreground">
                  {(action as { display_id?: string }).display_id || '—'}
                </span>
                <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm">
                  {action.name}
                </span>
                <Badge variant={actionStatusBadgeVariant(action.status)}>
                  {action.status || 'No status'}
                </Badge>
                <span
                  className={
                    overdue
                      ? 'tw:w-28 tw:shrink-0 tw:text-right tw:text-xs tw:text-destructive'
                      : 'tw:w-28 tw:shrink-0 tw:text-right tw:text-xs tw:text-muted-foreground'
                  }
                >
                  {formatActionDate(action.target_date)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Sibling dialog, matching how this modal already renders its confirms. */}
      {openAction && (
        <Suspense fallback={null}>
          <ActionDrawer
            scope={INCIDENT}
            selectedEntity={openAction}
            toggle={() => setOpenAction(null)}
          />
        </Suspense>
      )}
    </section>
  );
};

export default PositionTasksSection;
